import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  User as FirebaseUser
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import { User, AuthContextType } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const signup = async (email: string, password: string, userData: Omit<User, 'uid' | 'planStatus' | 'linkedTeachers' | 'linkedStudents'>) => {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    
    // Set initial plan status based on role
    const planStatus: 'active' | 'expired' = userData.role === 'teacher' ? 'active' : 'active';
    
    // Create user document in Firestore
    const userDoc: User = {
      ...userData,
      uid: user.uid,
      email: email, // Add email field to Firestore document
      planStatus,
      linkedTeachers: [],
      linkedStudents: [],
      practiceSessionDuration: 30, // Default 30 minutes
      practiceSessionsPerWeek: 5, // Default 5 times per week
      hasCompletedSetup: userData.hasCompletedSetup || false, // Will be set to true after profile setup
      weekStartDay: 'Sunday' as const // Fixed to Sunday for leaderboard consistency
    };
    
    await setDoc(doc(db, 'users', user.uid), userDoc);
    
    // Send verification email
    try {
      // Try custom verification email first
      const sendCustomVerificationEmail = httpsCallable(functions, 'sendCustomVerificationEmail');
      await sendCustomVerificationEmail({ 
        email: user.email,
        displayName: userData.name || userData.nickname 
      });
      console.log('Custom verification email sent successfully');
    } catch (error) {
      console.error('Failed to send custom verification email, falling back to default:', error);
      // Fallback to default Firebase verification email
      await sendEmailVerification(user);
      console.log('Default verification email sent as fallback');
    }
    
    return;
  };

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
    return;
  };

  const logout = async () => {
    await signOut(auth);
    return;
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
    return;
  };

  const resendEmailVerification = async () => {
    if (auth.currentUser) {
      try {
        // Try custom verification email first
        const sendCustomVerificationEmail = httpsCallable(functions, 'sendCustomVerificationEmail');
        await sendCustomVerificationEmail({ 
          email: auth.currentUser.email!,
          displayName: currentUser?.name || currentUser?.nickname 
        });
        console.log('Custom verification email resent successfully');
      } catch (error) {
        console.error('Failed to send custom verification email, falling back to default:', error);
        // Fallback to default Firebase verification email
        await sendEmailVerification(auth.currentUser);
        console.log('Default verification email resent as fallback');
      }
    }
    return;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        try {
          // Fetch user data from Firestore
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setCurrentUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              emailVerified: firebaseUser.emailVerified,
              ...userData
            } as User);
          } else {
            // For admin users, create a minimal user object if no document exists
            // No Firestore document found for user
            if (firebaseUser.email === (process.env.REACT_APP_ADMIN_EMAIL || 'mail@mindfulpracticebuddy.com.au')) {
              // Creating minimal admin user object (no Firestore document found)
              setCurrentUser({
                uid: firebaseUser.uid,
                email: firebaseUser.email || '',
                role: 'admin',
                name: 'Admin',
                nickname: 'Admin',
                emailVerified: firebaseUser.emailVerified
              } as User);
            } else if (firebaseUser.email === null) {
              // During SMS verification, email might be null temporarily
              // User email is null (likely during SMS verification), setting user to null temporarily
              setCurrentUser(null);
            } else {
              console.error('User document not found in Firestore for non-admin user:', firebaseUser.email);
              setCurrentUser(null);
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          // For admin users, create a minimal user object if no document exists
          if (firebaseUser.email === (process.env.REACT_APP_ADMIN_EMAIL || 'mail@mindfulpracticebuddy.com.au')) {
              // Creating minimal admin user object
            setCurrentUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              role: 'admin',
              name: 'Admin',
              nickname: 'Admin',
              emailVerified: firebaseUser.emailVerified
            } as User);
          } else {
            setCurrentUser(null);
          }
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const refreshUserData = async () => {
    if (auth.currentUser) {
      try {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const updatedUser = { 
            uid: auth.currentUser.uid, 
            email: auth.currentUser.email || '',
            emailVerified: auth.currentUser.emailVerified,
            ...userData 
          } as User;
          
          // If email verification status has changed, update Firestore
          if (userData.emailVerified !== auth.currentUser.emailVerified) {
            console.log('Email verification status changed, updating Firestore');
            await updateDoc(doc(db, 'users', auth.currentUser.uid), {
              emailVerified: auth.currentUser.emailVerified
            });
          }
          
          setCurrentUser(updatedUser);
        }
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }
    }
  };

  const value: AuthContextType = {
    currentUser,
    loading,
    signup,
    login,
    logout,
    resetPassword,
    resendEmailVerification,
    refreshUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
